/// <reference types="vite/client" />

import axios from 'axios';
import { store } from '../redux/store';
import { clearTokens, setTokens } from '../redux/reducers/authSlice';
import { apiEndpoints } from '../utils/api-endpoints';
import { TokenStorage } from '../utils/token-storage';

// Axios default instance for unauthenticated requests
const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

axios.defaults.baseURL = apiUrl;

export const isTokenExpiredIn = (token: string, minutes: number = 5): boolean => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= expirationTime - 1000 * 60 * minutes; // 5 minutes before expiration
  } catch (error) {
    return true;
  }
};

// Axios instance for authenticated requests
export const axiosInstance = axios.create({
  baseURL: apiUrl,
});

axiosInstance.interceptors.request.use(async (config) => {
  const { token: jwtToken, refreshToken: refreshTokenStored } = TokenStorage.getTokens();

  // Check if token is expired before making the request
  if (isTokenExpiredIn(jwtToken)) {
    try {
      // Try to refresh the token
      const response = await axios.post(apiEndpoints.auth.refreshToken, { refreshToken: refreshTokenStored });
      const { token, refreshToken, user } = response.data;
      store.dispatch(setTokens({ token, refreshToken, user }));
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      store.dispatch(clearTokens());
    }
  } else if (jwtToken) {
    config.headers.Authorization = `Bearer ${jwtToken}`;
  }
  
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only retry if we get a 401 and haven't already tried refreshing
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      store.dispatch(clearTokens());
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;